import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Space,
  Typography,
  Tabs,
  Divider,
  App,
  Modal
} from 'antd';
import {
  UserOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  ApiOutlined,
  MailOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  InfoCircleOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  SettingOutlined,
  AimOutlined
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import apiService from '../../services/apiService';
import TargetManagement from '../../components/TargetManagement';
import useSettingsStore from '../../store/settingsStore';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminSettingsPage = () => {
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const { updateSettings, fetchSettings } = useSettingsStore();
  const [tenantSettings, setTenantSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apolloSettings, setApolloSettings] = useState(null);
  const [apolloLoading, setApolloLoading] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState('');
  const [form] = Form.useForm();
  const [apolloForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  useEffect(() => {
    fetchTenantSettings();
    fetchApolloSettings();
    fetchEmailSettings();
    fetchSettings(); // Load system settings
  }, [fetchSettings]);

  const fetchTenantSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get('/admin/tenant/settings');
      const settings = response.data.data;
      setTenantSettings(settings);
      form.setFieldsValue(settings);
    } catch (error) {
      message.error('Failed to fetch tenant settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApolloSettings = async () => {
    setApolloLoading(true);
    try {
      const response = await apiService.get('/admin/integrations/apollo');
      const settings = response.data.data;
      setApolloSettings(settings);
      apolloForm.setFieldsValue(settings);
    } catch (error) {
      const defaultSettings = {
        apiKey: '',
        enabled: false,
        syncInterval: 24,
        autoSync: false
      };
      setApolloSettings(defaultSettings);
      apolloForm.setFieldsValue(defaultSettings);
    } finally {
      setApolloLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    setEmailLoading(true);
    try {
      const response = await apiService.get('/admin/email/settings');
      const settings = response.data.data;
      setEmailSettings(settings);
      emailForm.setFieldsValue(settings);
    } catch (error) {
      const defaultSettings = {
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        smtpSecure: false,
        senderName: '',
        senderEmail: '',
        enabled: false
      };
      setEmailSettings(defaultSettings);
      emailForm.setFieldsValue(defaultSettings);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSettingsUpdate = async (values) => {
    setIsLoading(true);
    try {
      console.log('Sending settings data:', values);
      await apiService.put('/admin/tenant/settings', values);
      
      // Update global settings if preferences changed
      if (values.preferences) {
        await updateSettings(values.preferences);
      }
      
      message.success('Settings updated successfully');
      await fetchTenantSettings();
      await fetchSettings(); // Refresh global settings
    } catch (error) {
      message.error('Failed to update settings');
      console.error('Settings update error:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApolloSettingsUpdate = async (values) => {
    setApolloLoading(true);
    try {
      await apiService.put('/admin/integrations/apollo', values);
      message.success('Apollo.io settings updated successfully');
      await fetchApolloSettings();
    } catch (error) {
      message.error('Failed to update Apollo.io settings');
    } finally {
      setApolloLoading(false);
    }
  };

  const handleEmailSettingsUpdate = async (values) => {
    setEmailLoading(true);
    try {
      await apiService.put('/admin/email/settings', values);
      message.success('Email settings updated successfully');
      await fetchEmailSettings();
    } catch (error) {
      message.error('Failed to update email settings');
    } finally {
      setEmailLoading(false);
    }
  };

  const testApolloConnection = async () => {
    setApolloLoading(true);
    try {
      await apiService.post('/admin/integrations/apollo/test');
      message.success('Apollo.io connection successful');
    } catch (error) {
      message.error('Apollo.io connection failed');
    } finally {
      setApolloLoading(false);
    }
  };

  const testEmailConnection = async () => {
    setEmailLoading(true);
    try {
      await apiService.post('/admin/email/test');
      message.success('Email connection successful');
    } catch (error) {
      message.error('Email connection failed');
    } finally {
      setEmailLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailLoading(true);
    try {
      await apiService.post('/admin/email/send-test');
      message.success('Test email sent successfully');
    } catch (error) {
      message.error('Failed to send test email');
    } finally {
      setEmailLoading(false);
    }
  };

  const editTemplate = (template) => {
    setCurrentTemplate(template);
    templateForm.setFieldsValue({
      subject: template.subject,
      body: template.body
    });
    setTemplateModalVisible(true);
  };

  const saveTemplate = async (values) => {
    try {
      await apiService.put(`/admin/email/templates/${currentTemplate.id}`, values);
      message.success('Template updated successfully');
      setTemplateModalVisible(false);
      await fetchEmailSettings();
    } catch (error) {
      message.error('Failed to update template');
    }
  };

  const previewTemplate = (template) => {
    setCurrentTemplate(template);
    setTemplatePreview(template.body);
    setPreviewModalVisible(true);
  };

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSettingsUpdate}
          initialValues={tenantSettings}
        >
          <Card title="Organization Information" className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name={['organization', 'name']}
                label="Organization Name"
                rules={[{ required: true, message: 'Organization name is required' }]}
              >
                <Input placeholder="Enter organization name" />
              </Form.Item>

              <Form.Item
                name={['organization', 'subdomain']}
                label="Subdomain"
                rules={[{ required: true, message: 'Subdomain is required' }]}
              >
                <Input 
                  placeholder="Enter subdomain" 
                  addonAfter=".crm.com"
                />
              </Form.Item>

              <Form.Item
                name={['organization', 'industry']}
                label="Industry"
              >
                <Select placeholder="Select industry">
                  <Option value="technology">Technology</Option>
                  <Option value="healthcare">Healthcare</Option>
                  <Option value="finance">Finance</Option>
                  <Option value="retail">Retail</Option>
                  <Option value="manufacturing">Manufacturing</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name={['organization', 'size']}
                label="Company Size"
              >
                <Select placeholder="Select company size">
                  <Option value="1-10">1-10 employees</Option>
                  <Option value="11-50">11-50 employees</Option>
                  <Option value="51-200">51-200 employees</Option>
                  <Option value="201-500">201-500 employees</Option>
                  <Option value="500+">500+ employees</Option>
                </Select>
              </Form.Item>
            </div>
          </Card>

          <Card title="System Preferences" className="mb-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Default Currency</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Currency used for deals and targets
                  </Text>
                </div>
                <Form.Item name={['preferences', 'currency']} className="mb-0">
                  <Select style={{ width: 120 }}>
                    <Option value="USD">USD ($)</Option>
                    <Option value="EUR">EUR (€)</Option>
                    <Option value="GBP">GBP (£)</Option>
                    <Option value="INR">INR (₹)</Option>
                    <Option value="JPY">JPY (¥)</Option>
                    <Option value="CAD">CAD (C$)</Option>
                    <Option value="AUD">AUD (A$)</Option>
                  </Select>
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Time Zone</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Default time zone for the organization
                  </Text>
                </div>
                <Form.Item name={['preferences', 'timezone']} className="mb-0">
                  <Select style={{ width: 200 }}>
                    <Option value="UTC">UTC</Option>
                    <Option value="America/New_York">Eastern Time</Option>
                    <Option value="America/Los_Angeles">Pacific Time</Option>
                    <Option value="Europe/London">London</Option>
                    <Option value="Asia/Kolkata">India</Option>
                  </Select>
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Date Format</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    How dates are displayed throughout the system
                  </Text>
                </div>
                <Form.Item name={['preferences', 'dateFormat']} className="mb-0">
                  <Select style={{ width: 150 }}>
                    <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                    <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                    <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>
          </Card>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isLoading}
              icon={<SaveOutlined />}
              className="focus-visible:focus"
            >
              Save General Settings
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'targets',
      label: (
        <span>
          <AimOutlined />
          Targets
        </span>
      ),
      children: <TargetManagement />
    }
  ];

  return (
    <App>
      <div className="space-y-6">
        <div>
          <Title level={2} className="mb-2">Admin Settings</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Configure system settings and tenant preferences
          </Text>
        </div>

        <Tabs 
          defaultActiveKey="general" 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
          items={tabItems}
        />
      </div>
    </App>
  );
};

export default AdminSettingsPage;
